import { createSlice } from "@reduxjs/toolkit";
import { generateUUID } from "../../utils/Utils";
import { entityIdsEqual, getMyCart } from "./utils";
import { toGetMyCart, toSaveMyCart, toDeleteMyCart } from "../actions/cart.actions";
import { SavedCartPayload } from "../../utils/cartPersistence";
import { getCartErrorMessage } from "../../utils/cartResponse";

const initialState = {
  vehicle: null as any,
  browseServicesWithoutVehicle: false,
  services: [] as any,
  products: [] as any,
  cart_id: "" as string,
  /** Selected branch id (`branches[].id`); undefined until user picks a branch on /order. */
  branch_id: undefined as number | undefined,
  /** ERP company id from selected branch (`branches[].company_id`). */
  company_id: undefined as number | undefined,
  my_cart: {} as any,
  appointment: null as any,
  showPaiement: false,
  loyaltyType: "autochapeau" as any,
  loyaltyPhone: null as any,
  donationAmount: 0 as number,
  walletAmount: 0 as number,
  amount: 0 as number,
  coupon: "" as string,
  discountAmount :0 as number,
  cartReady: false,
  cartLoading: false,
  cartRequestId: null as string | null,
  cartError: null as string | null,
  cartTotal: null as number | null,
};

function applyServerCart(state: typeof initialState, saved: SavedCartPayload | null) {
  const serverCartId = saved?.cart_id ?? "";
  if (state.cart_id !== serverCartId) {
    state.coupon = "";
    state.discountAmount = 0;
    state.walletAmount = 0;
  }
  if (state.cart_id !== serverCartId ||
      JSON.stringify(state.products) !== JSON.stringify(saved?.products ?? []) ||
      JSON.stringify(state.services) !== JSON.stringify(saved?.services ?? [])) {
    // Checkout must recalculate from the confirmed lines after a cart change.
    state.amount = 0;
  }
  state.cart_id = serverCartId;
  state.products = saved?.products ?? [];
  state.services = saved?.services ?? [];
  if (saved) {
    state.branch_id = saved.branch_id || undefined;
    state.company_id = saved.company_id || undefined;
    state.vehicle = saved.vehicle_id
      ? { ...(entityIdsEqual(saved.vehicle?.id, saved.vehicle_id) ? saved.vehicle : entityIdsEqual(state.vehicle?.id, saved.vehicle_id) ? state.vehicle : {}), id: saved.vehicle_id }
      : null;
    state.appointment = saved.appointment_slot_id
      ? { ...(entityIdsEqual(saved.appointment?.id, saved.appointment_slot_id) ? saved.appointment : entityIdsEqual(state.appointment?.id, saved.appointment_slot_id) ? state.appointment : {}), id: saved.appointment_slot_id }
      : null;
  }
  state.cartTotal = saved?.amount_total != null && Number.isFinite(Number(saved.amount_total))
    ? Number(saved.amount_total) : saved ? null : 0;
  state.cartReady = true;
  state.cartError = null;
  state.my_cart = getMyCart(state);
  if (state.products.length === 0 && state.services.length === 0) state.showPaiement = false;
}
export const gloablSlice = createSlice({
  name: "global",
  initialState: initialState,
  reducers: {
    setVehicle: (state, action) => {
      state.services = [];
      state.vehicle = action.payload;
      state.browseServicesWithoutVehicle = false;
      state.my_cart = getMyCart(state);
    },
    setBrowseServicesWithoutVehicle: (state, action: { payload: boolean }) => {
      state.browseServicesWithoutVehicle = action.payload;
    },
    toAddOrRemoveService: (state, action) => {
      const raw = action.payload;
      if (raw == null || raw.id === undefined || raw.id === null) {
        return;
      }
      const index = state.services.findIndex((el: any) =>
        entityIdsEqual(el.id, raw.id)
      );
      // A service is always priced/scheduled against a car — no vehicle, no add.
      // Callers open ChooseVehicleModal first; this is the backstop.
      if (index === -1 && !state.vehicle?.id) {
        return;
      }
      if (index === -1) {
        state.services.push({ ...raw });
      } else {
        // Re-selecting an existing service updates variant/tint details instead
        // of silently keeping the stale server draft line.
        state.services[index] = { ...state.services[index], ...raw };
      }
      if (state.cart_id === "") {
        const uuid = generateUUID();
        state.cart_id = uuid;
      }
      state.my_cart = getMyCart(state);
      state.walletAmount = 0;
    },
    toAddOrRemoveProduct: (state, action) => {
      const index = state.products.findIndex((el: any) =>
        entityIdsEqual(el.id, action.payload.id)
      );
      if (index !== -1) {
        state.products[index].qty += 1;
      } else {
        const product = action.payload;
        state.products.push({ ...product, qty: product.qty ?? 1 });
      }
      if (state.cart_id === "") {
        const uuid = generateUUID();
        state.cart_id = uuid;
      }

      state.my_cart = getMyCart(state);
      state.walletAmount = 0;
    },
    toRemoveService: (state, action) => {
      const rid =
        action.payload != null && typeof action.payload === "object"
          ? (action.payload as any).id
          : action.payload;
      state.services = state.services.filter(
        (el: any) => !entityIdsEqual(el.id, rid)
      );
      state.my_cart = getMyCart(state);
      state.walletAmount = 0;
    },
    toRemoveProduct: (state, action) => {
      state.products = state.products.filter(
        (el: any) => !entityIdsEqual(el.id, action.payload.id)
      );
      state.my_cart = getMyCart(state);
      state.walletAmount = 0;
    },

    toUpdateQuantity: (state, action) => {
      const index = state.products.findIndex((el: any) =>
        entityIdsEqual(el.id, action.payload.id)
      );
      if (index !== -1) {
        state.products[index].qty = action.payload.qty;
      }

      state.my_cart = getMyCart(state);
    },

    setServices: (state, action) => {
      state.services = action.payload;
      state.my_cart = getMyCart(state);
    },
    setAppointment: (state, action) => {
      state.appointment = action.payload;
      state.my_cart = getMyCart(state);
    },
    setProducts: (state, action) => {
      state.products = action.payload;
      state.my_cart = getMyCart(state);
    },
    setCartId: (state, action) => {
      state.cart_id = action.payload;

      state.my_cart = getMyCart(state);
    },
    /** Server contents always replace stale browser contents, including an empty cart. */
    hydrateCart: (state, action) => {
      applyServerCart(state, action.payload);
    },
    setShowPaiement: (state, action) => {
      state.showPaiement = action.payload;
    },
    setSelectedBranch: (
      state,
      action: { payload: { branch_id: number; company_id: number } }
    ) => {
      state.branch_id = action.payload.branch_id;
      state.company_id = action.payload.company_id;
      state.my_cart = getMyCart(state);
    },
    resetState: (state) => {
      state.cartReady = false;
      state.cartLoading = false;
      state.cartRequestId = null;
      state.cartError = null;
      state.cartTotal = null;
      state.vehicle = null;
      state.browseServicesWithoutVehicle = false;
      state.services = [];
      state.products = [];
      state.cart_id = "";
      state.branch_id = undefined;
      state.company_id = undefined;
      state.my_cart = {};
      state.appointment = null;
      state.donationAmount = 0;
      state.walletAmount = 0;
      state.loyaltyType = "autochapeau";
      state.coupon ="";
      state.discountAmount = 0;
    },
    setLoyaltyType: (state, action) => {
      state.loyaltyType = action.payload;
    },
    setLoyaltyPhone: (state, action) => {
      state.loyaltyPhone = action.payload;
    },
    setDonationAmount: (state, action) => {
      state.donationAmount = action.payload;
    },
    setWalletAmount: (state, action) => {
      state.walletAmount = action.payload;
    },
    setAmountToDisplay: (state, action) => {
      state.amount = action.payload;
    },
    setCoupon: (state, action) => {
      state.coupon = action.payload;
    },
     setDiscountAmount: (state, action) => {
      state.discountAmount = action.payload;
    },
  },

  extraReducers: (builder) => {
    for (const thunk of [toGetMyCart, toSaveMyCart, toDeleteMyCart]) {
      builder.addCase(thunk.pending, (state, action) => {
        state.cartLoading = true;
        state.cartRequestId = action.meta.requestId;
        state.cartError = null;
      });
      builder.addCase(thunk.fulfilled, (state, action) => {
        // Ignore a response superseded by another request or logout.
        if (state.cartRequestId !== action.meta.requestId) return;
        applyServerCart(state, action.payload);
        state.cartLoading = false;
        state.cartRequestId = null;
      });
      builder.addCase(thunk.rejected, (state, action) => {
        if (state.cartRequestId !== action.meta.requestId) return;
        state.cartLoading = false;
        state.cartRequestId = null;
        state.cartError = getCartErrorMessage(action.payload) ?? "cart-update-failed";
      });
    }
  },
});

export const {
  setAmountToDisplay,
  setVehicle,
  setBrowseServicesWithoutVehicle,
  toAddOrRemoveService,
  setServices,
  setProducts,
  toAddOrRemoveProduct,
  setCartId,
  setSelectedBranch,
  setAppointment,
  setShowPaiement,
  hydrateCart,
  resetState,
  toUpdateQuantity,
  toRemoveProduct,
  toRemoveService,
  setLoyaltyType,
  setLoyaltyPhone,
  setDonationAmount,
  setWalletAmount,
  setCoupon,
  setDiscountAmount
} = gloablSlice.actions;

export default gloablSlice.reducer;
